import React, { useState } from "react";
import { LocalizationProvider, DateTimePicker } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFnsV3";
import { Controller, Control } from "react-hook-form";
import { TextFieldProps } from "@mui/material";
import { FormFactoryDateRules } from "@/modules/common/types";
import { isAfter, isBefore } from "date-fns";
import { FieldsResponse } from "@/modules/common/dtos/index.v2";

interface DatePickerRendererProps {
  fullName: string;
  control: Control<any>;
  fieldRules: FormFactoryDateRules;
  commonProps: any;
  status_form_field: FieldsResponse;
}

const DatePickerRenderer: React.FC<DatePickerRendererProps> = ({
  fullName,
  control,
  fieldRules,
  commonProps,
  status_form_field,
}) => {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const formatDate = (date: Date) => {
    return date.toLocaleString("en-US", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hour12: false,
    });
  };
  const validateDate = (value: any): string | true => {
    if (!value) {
      setErrorMessage("Valid date is required");
      return "Valid date is required";
    }

    const dateValue = value instanceof Date ? value : new Date(value);
    if (isNaN(dateValue.getTime())) {
      setErrorMessage("Valid date is required");
      return "Valid date is required";
    }

    if (fieldRules.minDate && isBefore(dateValue, fieldRules.minDate)) {
      setErrorMessage(`Date must be after ${formatDate(fieldRules.minDate)}`);
      return `Date must be after ${formatDate(fieldRules.minDate)}`;
    }

    if (fieldRules.maxDate && isAfter(dateValue, fieldRules.maxDate)) {
      setErrorMessage(`Date must be before ${formatDate(fieldRules.maxDate)}`);
      return `Date must be before ${formatDate(fieldRules.maxDate)}`;
    }

    setErrorMessage(null);
    return true;
  };

  const defaultDate = fieldRules?.defaultValue
    ? new Date(fieldRules.defaultValue)
    : null;

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Controller
        name={fullName}
        control={control}
        rules={{
          validate: validateDate,
        }}
        render={({ field: { onChange, value } }) => (
          <DateTimePicker
            label={status_form_field.field.label}
            value={value ? new Date(value) : defaultDate || null}
            onChange={(newValue: Date | null) => {
              onChange(newValue);

              validateDate(newValue);
            }}
            slotProps={{
              textField: {
                ...commonProps,
                error: !!errorMessage,
                helperText: errorMessage || " ",
                inputProps: {
                  ...commonProps.inputProps,
                  readOnly: true,
                },
              } as TextFieldProps,
            }}
          />
        )}
      />
    </LocalizationProvider>
  );
};

export default DatePickerRenderer;
